import React, { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Alert,
} from "@mui/material";
import { Axios } from "../../../Api/axios";
import Cookie from "cookie-universal";
import { CLIENTS, SERVICES, TAGS } from "../../../Api/Api";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TablePagination from "@mui/material/TablePagination";
import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import TextEditor from "./TextEditor";

const cookie = Cookie();
const token = cookie.get("dashboardLogin");
const userRole = cookie.get("userRole");

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#098040",
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

export default function TagsTable() {
  const [alert, setAlert] = useState(null);
  const [alertVisible, setAlertVisible] = useState(true);
  const [tags, setTags] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    id: null,
    name_en: "",
    name_ar: "",
    slug_en: "",
    slug_ar: "",
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("id");

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    try {
      const response = await Axios.get(`/panel/${TAGS}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-User-Role": userRole,
        },
      });
      setTags(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - tags.length) : 0;

  const visibleRows = React.useMemo(
    () =>
      [...tags]
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, tags]
  );

  const slugify = (text) => {
    return text
      .toString()
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-") // Replace spaces with -
      .replace(/[^a-z0-9\u0600-\u06FF\-]+/g, "") // Keep Arabic, English letters, numbers, and dashes
      .replace(/\-\-+/g, "-"); // Replace multiple - with single -
  };

  const handleEdit = (id) => {
    const tag = tags.find((row) => row.id === id);
    setEditData({
      id: tag.id,
      name_en: tag.name_en,
      name_ar: tag.name_ar,
      slug_en: tag.slug_en,
      slug_ar: tag.slug_ar,
    });
    setIsEditing(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditData((prev) => ({
      ...prev,
      [name]: value,
      slug_en: name === "name_en" ? slugify(value) : prev.slug_en,
      slug_ar: name === "name_ar" ? slugify(value) : prev.slug_ar,
    }));
  };

  const handleUpdate = async () => {
    if (!window.confirm("Are you sure you want to update this client?")) return;

    const formData = new FormData();
    formData.append("name_en", editData.name_en);
    formData.append("name_ar", editData.name_ar);
    formData.append("slug_en", editData.slug_en);
    formData.append("slug_ar", editData.slug_ar);
    formData.append("_method", "PUT");

    try {
      const response = await Axios.post(
        `/panel/${TAGS}/${editData.id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-User-Role": userRole,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setTags((prevTags) =>
        prevTags.map((tag) =>
          tag.id === editData.id ? { ...tag, ...editData } : tag
        )
      );

      setAlert({ message: "Tag updated successfully", severity: "success" });
      handleCloseEdit();
    } catch (error) {
      console.error("Error updating Tag:", error);
      setAlert({ message: "Error updating Tag", severity: "error" });
    }

    setTimeout(() => setAlertVisible(false), 2000);
    setAlertVisible(true);
  };

  const handleCloseEdit = () => {
    setIsEditing(false);
    setEditData({
      id: null,
      name_en: "",
      name_ar: "",
      slug_en: "",
      slug_ar: "",
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this client?")) return;

    try {
      await Axios.delete(`/panel/${TAGS}/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-User-Role": userRole,
        },
      });

      setTags(tags.filter((tag) => tag.id !== id));
      setAlert({ message: "Tag deleted successfully", severity: "success" });
    } catch (error) {
      console.error("Error deleting tag:", error);
      setAlert({ message: "Error deleting Tag", severity: "error" });
    }

    setTimeout(() => setAlertVisible(false), 2000);
    setAlertVisible(true);
  };

  return (
    <Box sx={{ height: "100%", width: "100%" }}>
      {alert && alertVisible && (
        <Alert
          severity={alert.severity}
          onClose={() => setAlertVisible(false)}
          sx={{
            transition: "opacity 1s ease-out",
            opacity: alertVisible ? 1 : 0,
          }}
        >
          {alert.message}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 700 }} aria-label="customized table">
          <TableHead>
            <TableRow>
              <StyledTableCell
                onClick={(e) => handleRequestSort(e, "id")}
                style={{ cursor: "pointer" }}
              >
                ID
                {orderBy === "id" ? (
                  order === "desc" ? (
                    <ArrowDownward />
                  ) : (
                    <ArrowUpward />
                  )
                ) : orderBy === "id" && order === "asc" ? (
                  <ArrowUpward />
                ) : (
                  <ArrowDownward />
                )}
              </StyledTableCell>

              <StyledTableCell
                onClick={(e) => handleRequestSort(e, "name_en")}
                style={{ cursor: "pointer" }}
              >
                Name (English)
                {orderBy === "name_en" ? (
                  order === "desc" ? (
                    <ArrowDownward />
                  ) : (
                    <ArrowUpward />
                  )
                ) : orderBy === "name_en" && order === "asc" ? (
                  <ArrowUpward />
                ) : (
                  <ArrowDownward />
                )}
              </StyledTableCell>

              <StyledTableCell
                onClick={(e) => handleRequestSort(e, "name_ar")}
                style={{ cursor: "pointer" }}
              >
                الاسم
                {orderBy === "name_ar" ? (
                  order === "desc" ? (
                    <ArrowDownward />
                  ) : (
                    <ArrowUpward />
                  )
                ) : orderBy === "name_ar" && order === "asc" ? (
                  <ArrowUpward />
                ) : (
                  <ArrowDownward />
                )}
              </StyledTableCell>
              <StyledTableCell
                onClick={(e) => handleRequestSort(e, "slug_en")}
                style={{ cursor: "pointer" }}
              >
                Slug
                {orderBy === "slug_en" ? (
                  order === "desc" ? (
                    <ArrowDownward />
                  ) : (
                    <ArrowUpward />
                  )
                ) : orderBy === "slug_en" && order === "asc" ? (
                  <ArrowUpward />
                ) : (
                  <ArrowDownward />
                )}
              </StyledTableCell>
              <StyledTableCell
                onClick={(e) => handleRequestSort(e, "slug_ar")}
                style={{ cursor: "pointer" }}
              >
                Slug Ar
                {orderBy === "slug_ar" ? (
                  order === "desc" ? (
                    <ArrowDownward />
                  ) : (
                    <ArrowUpward />
                  )
                ) : orderBy === "slug_ar" && order === "asc" ? (
                  <ArrowUpward />
                ) : (
                  <ArrowDownward />
                )}
              </StyledTableCell>
              <StyledTableCell>Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((tag) => (
              <StyledTableRow key={tag.id}>
                <StyledTableCell>{tag.id}</StyledTableCell>
                <StyledTableCell>{tag.name_en}</StyledTableCell>
                <StyledTableCell>{tag.name_ar}</StyledTableCell>
                <StyledTableCell>{tag.slug_en}</StyledTableCell>
                <StyledTableCell>{tag.slug_ar}</StyledTableCell>
                <StyledTableCell>
                  <IconButton
                    onClick={() => handleEdit(tag.id)}
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDelete(tag.id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={tags.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <Dialog open={isEditing} onClose={handleCloseEdit}>
        <DialogTitle>Edit Tag</DialogTitle>
        <DialogContent>
          <TextField
            label="Name (English)"
            name="name_en"
            value={editData.name_en}
            onChange={handleInputChange}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Slug (English)"
            name="slug_en"
            value={editData.slug_en}
            fullWidth
            margin="dense"
            disabled
          />

          <TextField
            label="الاسم (Arabic)"
            name="name_ar"
            value={editData.name_ar}
            onChange={handleInputChange}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Slug (Arabic)"
            name="slug_ar"
            value={editData.slug_ar}
            fullWidth
            margin="dense"
            disabled
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit} color="primary">
            Cancel
          </Button>
          <Button onClick={handleUpdate} color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}
